/**
 * Row arithmetic for the Reverse Vesting section of the termination modal.
 *
 * Kept out of the component so the four derived columns can be asserted directly, and so the
 * acceleration ceiling the inline error uses is the same expression the totals use.
 */

/** The mirror of `ReverseVestingTerminationSettlement`, minus what only the backend can settle. */
export type ReverseVestingTerminationRowTotals = {
  compensation: number;
  finalVestedShares: number;
  lapsedShares: number;
};

export type ReverseVestingTerminationRowInput = {
  acceleratedShares?: number;
  contractualVestedShares: number;
  /**
   * Schedule shares no longer held (buyback, secondary, class conversion) — the backend attributes
   * them vested-first, so they are neither kept nor repurchased.
   */
  disposedShares?: number;
  repurchasePrice?: number;
  /** Shares subject to reverse vesting on the row — the schedule's full share count. */
  reverseVestingShares: number;
};

/**
 * Compensation multiplies an integer share count by a price carrying up to 7 decimals, so binary
 * floating point leaves artifacts (0.0001 × 2500 lands on 0.25000000000000006). Rounding back to
 * the price's own precision is what keeps this equal to the figure the backend stores.
 */
const COMPENSATION_PRECISION = 1e7;

export const reverseVestingTerminationRowTotals = ({
  acceleratedShares,
  contractualVestedShares,
  disposedShares = 0,
  repurchasePrice,
  reverseVestingShares,
}: ReverseVestingTerminationRowInput): ReverseVestingTerminationRowTotals => {
  const accelerated = Number.isFinite(acceleratedShares) ? Number(acceleratedShares) : 0;
  const price = Number.isFinite(repurchasePrice) ? Number(repurchasePrice) : 0;
  // Only the shares still held can be kept or repurchased.
  const heldShares = Math.max(reverseVestingShares - disposedShares, 0);

  // Clamped rather than trusted: an over-cap Accelerated is a validation error the operator still
  // has to fix, and an unclamped Final Vested would render above Issued while they read the error.
  const finalVestedShares = Math.min(
    Math.max(contractualVestedShares + accelerated, 0),
    heldShares,
  );
  const lapsedShares = Math.max(heldShares - finalVestedShares, 0);

  return {
    compensation:
      Math.round(price * lapsedShares * COMPENSATION_PRECISION) / COMPENSATION_PRECISION,
    finalVestedShares,
    lapsedShares,
  };
};

/**
 * AC: Accelerated cannot exceed the row's Contractual Unvested Shares. Returns true when the field
 * should be flagged, so the caller decides how to surface it.
 */
export const isAcceleratedShareCountOverCap = ({
  acceleratedShares,
  contractualUnvestedShares,
}: {
  acceleratedShares?: number;
  contractualUnvestedShares: number;
}): boolean =>
  Number.isFinite(acceleratedShares) && Number(acceleratedShares) > contractualUnvestedShares;
